import { useState, useEffect, useMemo, useCallback, useLayoutEffect } from 'react';
import {
  AreaChart, Area, Tooltip, ReferenceLine, ResponsiveContainer, XAxis, YAxis,
} from 'recharts';
import { ArrowRight, ArrowDown, Eye, EyeOff } from 'lucide-react';
import Pagination from '../../../components/Pagination/Pagination';
import InfoTooltip from '../../../components/InfoTooltip/InfoTooltip';
import api from '../../../services/api';
import { profileFor } from './masterProfiles';
import styles from './PowIndex.module.css';

const PERIODS = ['1D', '1W', '1M', '3M', 'YTD', 'ALL'];

/* Qualifies the change under the balance — it's the change across the selected
   window, so the wording has to follow the tab. */
const PERIOD_LABELS = {
  '1D':  'past day',
  '1W':  'past week',
  '1M':  'past month',
  '3M':  'past 3 months',
  'YTD': 'year-to-date',
  'ALL': 'all-time',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Rows per page in the trade tables. Fixed rather than measured: the tables sit
   below the fold, so there is no window height to fit them to. */
const TRADES_PER_PAGE = 6;

/* ── Formatting ── */
const dollars = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// Sign ahead of the symbol — "-$62.52", not "$-62.52".
const signedDollars = (n) =>
  `${Number(n) < 0 ? '-' : '+'}${dollars(Math.abs(Number(n) || 0))}`;
const pct = (n, { sign = false } = {}) =>
  n == null ? '-' : `${sign && Number(n) > 0 ? '+' : ''}${(Number(n) || 0).toFixed(2)}%`;

// The header figures arrive as numbers from the detail endpoint but
// pre-formatted ("10.92%") on the master payload the page starts from, so either
// shape has to parse.
const numOf = (v) => {
  if (v == null || v === '') return null;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[^0-9.+-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

const price = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// "Jul 24, 2026 08:16" — UTC, matching the times TradeSync reports.
const tradeTime = (iso) => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  return `${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })} ` +
         `${d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC' })}`;
};

// Lots come back at broker precision (0.00119999); show them as sent rather than
// rounded, so a copied size is recognisable against the platform.
const lotsText = (n) => String(n ?? '-');

const riskTone = (risk) => {
  const r = String(risk || '').toLowerCase();
  if (r.includes('conserv') || r.includes('low')) return 'low';
  if (r.includes('aggress') || r.includes('high')) return 'high';
  return 'mid';
};

// Every percentage on this page carries its direction in its colour: red below
// zero, green above it, and the plain text colour at exactly zero or with
// nothing to show.
const toneOf = (n) => {
  if (n == null || !Number.isFinite(Number(n))) return '';
  if (Number(n) > 0) return styles.up;
  if (Number(n) < 0) return styles.down;
  return '';
};

/* ── Bridges recharts hover state out to the balance above the chart ── */
function TooltipBridge({ active, payload, onUpdate }) {
  const point = active && payload?.length ? payload[0].payload : null;
  useLayoutEffect(() => { onUpdate(point); });
  return null;
}

function useIsDark() {
  const check = () => {
    const attr = document.documentElement.getAttribute('data-theme');
    return attr === 'dark' || (attr !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  };
  const [dark, setDark] = useState(check);
  useEffect(() => {
    const obs = new MutationObserver(() => setDark(check()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return dark;
}

/**
 * The POW Index itself: everything above the copied accounts on the trade
 * copier. One strategy, one page — the header, the headline figures, the
 * performance card, the monthly returns and the trade tables.
 *
 * `showCta` is whether the user still has a trading account off the copier —
 * one never connected, or one since disconnected. Someone whose every account
 * is already copying has nothing left to start, and adjusts through Manage.
 *
 * `children` is the user's own side of the page — the copied accounts — placed
 * under the trade tables and above the phone's pointer to the desktop view, so
 * a phone reads figures, then its accounts, then the hint.
 */
function PowIndex({ master, showCta, onStartCopying, children }) {
  const isDark = useIsDark();
  // The page is named before the master is resolved, so the editorial copy is
  // looked up under that same name — otherwise a page with no master behind it
  // would fall to the generic profile and lose its blurb along with its figures.
  const name = master?.name || 'POW Index';
  const profile = profileFor(name);
  // A user's own CEM master copies into POW Index rather than itself, so its
  // copier count is meaningless and the column is dropped.
  const isCemMaster = !!master?.copyMasterId && master.copyMasterId !== master.id;

  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const [period, setPeriod]         = useState('1D');
  const [chart, setChart]           = useState({ series: [], change: 0 });
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [activePoint, setActivePoint] = useState(null);

  const [returns, setReturns] = useState({ years: [], total: null, stats: {} });

  const [tab, setTab]               = useState('open'); // 'open' | 'history'
  const [tradesPage, setTradesPage] = useState(1);
  const [trades, setTrades]         = useState({ trades: [], pages: 1, total: 0 });
  const [tradesLoading, setTradesLoading] = useState(true);
  const [openCount, setOpenCount]   = useState(null);

  const masterId = master?.id;

  const handleTooltipUpdate = useCallback((point) => {
    setActivePoint(prev => (prev === point ? prev : point));
  }, []);

  /* Header figures */
  useEffect(() => {
    // Nothing to fetch without a master, and the page is not waiting on
    // anything — leaving this true would sit "Loading POW Index…" under a page
    // that has already settled into its empty state.
    if (!masterId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/copier/masters/${masterId}/detail`)
      .then(({ data }) => { setDetail(data); setError(''); })
      .catch(() => setError('Failed to load POW Index'))
      .finally(() => setLoading(false));
  }, [masterId]);

  /* Equity curve for the selected range */
  useEffect(() => {
    if (!masterId) return;
    api.get(`/copier/masters/${masterId}/chart`, { params: { range: period } })
      .then(({ data }) => setChart({
        series: Array.isArray(data.series) ? data.series : [],
        change: Number(data.change) || 0,
      }))
      .catch(() => setChart({ series: [], change: 0 }));
  }, [masterId, period]);

  /* Monthly returns table + the risk statistics beside the chart. One request:
     both are cuts of the same return history. */
  useEffect(() => {
    if (!masterId) return;
    api.get(`/copier/masters/${masterId}/returns`)
      .then(({ data }) => setReturns({
        years: Array.isArray(data.years) ? data.years : [],
        total: data.total ?? null,
        stats: data.stats || {},
      }))
      .catch(() => setReturns({ years: [], total: null, stats: {} }));
  }, [masterId]);

  /* The count beside the "Open positions" tab, which has to be right even while
     the history tab is the one being paged. One row is enough — the response
     carries the total. */
  useEffect(() => {
    if (!masterId) return;
    api.get(`/copier/masters/${masterId}/trades`, { params: { state: 'open', limit: 1 } })
      .then(({ data }) => setOpenCount(Number(data.total) || 0))
      .catch(() => setOpenCount(null));
  }, [masterId]);

  /* The active tab's page of trades */
  useEffect(() => {
    // As above: settle the table into "No open positions" rather than leaving
    // it reading "Loading trades…" against a master that doesn't exist.
    if (!masterId) { setTradesLoading(false); return; }
    setTradesLoading(true);
    api.get(`/copier/masters/${masterId}/trades`, {
      params: { state: tab === 'open' ? 'open' : 'closed', page: tradesPage, limit: TRADES_PER_PAGE },
    })
      .then(({ data }) => setTrades({
        trades: Array.isArray(data.trades) ? data.trades : [],
        pages: Number(data.pages) || 1,
        total: Number(data.total) || 0,
      }))
      .catch(() => setTrades({ trades: [], pages: 1, total: 0 }))
      .finally(() => setTradesLoading(false));
  }, [masterId, tab, tradesPage]);

  /* Two shapes behind one line, both measured against a zero baseline:

     1W and longer plot TradeSync's own `running_growth` for the day, straight
     from /analyses/{lead}/dailies with no arithmetic of ours on top — so the
     line is growth since inception, in percent, and 0 is break-even.

     1D has no daily to read (the day isn't closed yet), so it keeps plotting
     the intraday return in dollars against yesterday's close. */
  const isGrowthChart = chart.series.some(p => p.growth != null);

  const chartData = useMemo(
    () => chart.series
      // Belt and braces with the server, which already drops these: on a growth
      // chart a day without a running_growth has no value to plot, and falling
      // back to the dollar return would spike the line.
      .filter(p => !isGrowthChart || p.growth != null)
      .map(p => ({
        date: p.label,
        balance: Number(p.balance),
        base: Number(p.base),
        growth: p.growth == null ? null : Number(p.growth),
        value: p.growth == null ? Number(p.equity) - Number(p.base) : Number(p.growth),
        refValue: 0,
      })),
    [chart.series, isGrowthChart]
  );

  /* What the key above the chart reads. The hovered point while there is one,
     and otherwise the last point on the line — not to show, but so the row is
     laid out at its true height while it is hidden. Rendering it only on hover
     would push the chart and the risk statistics down the moment the pointer
     entered the card, which is the one time everything needs to hold still. */
  const metaPoint = activePoint || chartData[chartData.length - 1] || null;

  // Zero is forced into the domain so the baseline stays visible even when every
  // point sits on one side of it.
  const returnDomain = useMemo(() => {
    const values = chartData.map(d => d.value);
    const lo = Math.min(0, ...values);
    const hi = Math.max(0, ...values);
    const pad = (hi - lo) * 0.12 || 1;
    return [lo - pad, hi + pad];
  }, [chartData]);

  // Editorial copy, in preference order: what an admin typed on the master form,
  // what the master payload already carried, then the built-in blurb for the
  // strategy. It is the page's own subtitle now, so there is no About card.
  const about = detail?.description || master?.description || profile.about;
  const risk  = detail?.risk || master?.risk || profile.risk;

  const growth      = numOf(detail?.growth ?? master?.growth);
  const months      = detail?.trackMonths ?? master?.trackMonths;
  const headBalance = activePoint?.balance ?? detail?.balance ?? null;
  const changeDown  = chart.change < 0;

  const switchTab = (next) => { setTab(next); setTradesPage(1); };

  const rows = trades.trades;

  // Whether there is a master behind the page at all. Nothing to copy until
  // there is, so the CTA goes with the panels it belongs to.
  const available = Boolean(masterId);
  const canCopy   = showCta && available;

  // "+/- 0.67%" — a spread rather than a direction, so it stays the plain text
  // colour instead of being read as a gain.
  const spread = (n) => (n == null ? '-' : `+/- ${Number(n).toFixed(2)}%`);

  return (
    <div className={styles.page}>
      {/* Header — the strategy names the page now that it is the only one */}
      <div className={styles.header}>
        <h1 className={styles.title}>{name}</h1>
        {about && <p className={styles.subtitle}>{about}</p>}
      </div>

      {/* ── Risk tier + headline figures ── */}
      <section className={styles.headCard}>
        <div className={styles.headTop}>
          {risk ? (
            <span className={`${styles.riskPill} ${styles[`risk_${riskTone(risk)}`]}`}>
              Risk: <span className={styles.riskValue}>{risk}</span>
            </span>
          ) : <span />}
          {canCopy && (
            <button className={`${styles.copyBtn} ${styles.desktopOnly}`} onClick={() => onStartCopying(master)}>
              Start copying
            </button>
          )}
        </div>

        <div className={`${styles.statRow} ${isCemMaster ? styles.statRowFour : ''}`}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>GROWTH</span>
            <p className={`${styles.statValue} ${toneOf(growth)}`}>{pct(growth, { sign: true })}</p>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>ACCOUNT AGE</span>
            <p className={styles.statValue}>{months == null ? '-' : `${months} months`}</p>
          </div>
          {!isCemMaster && (
            <div className={styles.stat}>
              <span className={styles.statLabel}>COPIERS</span>
              <p className={styles.statValue}>{detail?.copiers ?? master?.followers ?? '-'}</p>
            </div>
          )}
          <div className={styles.stat}>
            <span className={styles.statLabel}>EQUITY</span>
            <p className={styles.statValue}>{detail?.equity == null ? '-' : dollars(detail.equity)}</p>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>
              MDD
              <InfoTooltip
                title="Maximum drawdown"
                text="The largest fall in this account's equity against its deposit since it started trading. It only ever moves further down, so it reflects the worst point reached, not where the account stands today."
              />
            </span>
            <p className={`${styles.statValue} ${toneOf(detail?.maxDD)}`}>
              {detail?.maxDD == null ? '-' : pct(detail.maxDD)}
            </p>
          </div>
        </div>

        {canCopy && (
          <button
            className={`${styles.copyBtn} ${styles.copyBtnMobile} ${styles.mobileOnly}`}
            onClick={() => onStartCopying(master)}
          >
            Start copying
          </button>
        )}
      </section>

      {error && <p className={styles.message}>{error}</p>}
      {loading && !detail && <p className={styles.message}>Loading POW Index…</p>}

      {/* No master to read yet — the desk creates the POW Index account in the
          admin Trade copier and this page resolves it by name. Said once, here,
          with the whole page still laid out beneath it: the panels each hold
          their own shape empty, which shows what the page will be rather than
          hiding it until the account exists. */}
      {!available && <p className={styles.message}>POW Index isn&rsquo;t available yet.</p>}

      {/* ── Performance ── */}
      <section className={`${styles.chartCard} ${styles.desktopBlock}`}>
        <div className={styles.chartHead}>
          <div>
            <div className={styles.balanceRow}>
              <span className={styles.balanceValue}>
                {balanceVisible ? dollars(headBalance) : '$·······'}
              </span>
              <span className={styles.balanceCurrency}>USD</span>
              <button
                className={styles.eyeBtn}
                onClick={() => setBalanceVisible(v => !v)}
                aria-label={balanceVisible ? 'Hide balance' : 'Show balance'}
              >
                {balanceVisible ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div className={`${styles.changeRow} ${changeDown ? styles.changeDown : ''}`}>
              <span>{balanceVisible ? dollars(chart.change) : '$·····'}</span>
              <span>{PERIOD_LABELS[period]}</span>
              {changeDown ? <ArrowDown size={12} /> : <ArrowRight size={12} />}
            </div>
          </div>

          <div className={styles.periodTabs}>
            {PERIODS.map(p => (
              <button
                key={p}
                className={`${styles.periodTab} ${period === p ? styles.periodTabActive : ''}`}
                onClick={() => setPeriod(p)}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        {metaPoint && (
          <div className={`${styles.chartMeta} ${activePoint ? '' : styles.chartMetaIdle}`}>
            <div className={styles.chartDate}>{metaPoint.date}</div>

            {/* Same key as the assets chart: says what the green line and the
                dashed baseline each are, and what they read at this point. */}
            <div className={styles.chartLegend}>
              <div className={styles.legendItem}>
                <span className={styles.legendLabel}>
                  <span className={styles.legendIcon}>
                    <span className={styles.legendLine} />
                    <span className={styles.legendDot} />
                    <span className={styles.legendLine} />
                  </span>
                  {isGrowthChart ? 'Growth' : 'Return'}
                </span>
                <span className={styles.legendValue}>
                  {isGrowthChart
                    ? pct(metaPoint.growth, { sign: true })
                    : signedDollars(metaPoint.value)}
                </span>
              </div>
              <div className={styles.legendItem}>
                <span className={styles.legendLabel}>
                  <span className={styles.legendIcon}>
                    <span className={styles.legendLineDashed} />
                    <span className={styles.legendSquare} />
                    <span className={styles.legendLineDashed} />
                  </span>
                  {isGrowthChart ? 'Break-even' : 'Starting equity'}
                </span>
                <span className={styles.legendValue}>
                  {isGrowthChart ? '0.00%' : dollars(metaPoint.base)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* The chart and the spread of its returns, side by side: the line says
            where the strategy went, the panel beside it how much it moved about
            on the way. */}
        <div className={styles.chartBody}>
          <div className={styles.chartArea}>
            {chartData.length === 0 ? (
              <div className={styles.chartEmpty}>No history for this range yet.</div>
            ) : (
              <ResponsiveContainer width="100%" height={330}>
                <AreaChart data={chartData} margin={{ top: 24, right: 0, left: 0, bottom: 8 }}>
                  <XAxis dataKey="date" hide axisLine={false} tickLine={false} />
                  <YAxis hide domain={returnDomain} />
                  <Tooltip
                    content={<TooltipBridge onUpdate={handleTooltipUpdate} />}
                    cursor={{ stroke: isDark ? '#B0B5B5' : '#525C5C', strokeWidth: 1 }}
                  />
                  <ReferenceLine y={0} stroke={isDark ? '#B0B5B5' : '#525C5C'} strokeDasharray="4 4" />
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#83C35F"
                    strokeWidth={2}
                    fill="none"
                    dot={false}
                    activeDot={{ r: 5, fill: '#83C35F', stroke: isDark ? '#111111' : '#FFFFFF', strokeWidth: 2 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>

          <div className={styles.riskStats}>
            <h3 className={styles.riskStatsTitle}>Risk statistics</h3>
            <div className={styles.riskStat}>
              <span className={styles.statLabel}>AVERAGE DAILY RETURN</span>
              <p className={styles.statValue}>{spread(returns.stats.daily)}</p>
            </div>
            <div className={styles.riskStat}>
              <span className={styles.statLabel}>AVERAGE WEEKLY RETURN</span>
              <p className={styles.statValue}>{spread(returns.stats.weekly)}</p>
            </div>
            <div className={styles.riskStat}>
              <span className={styles.statLabel}>AVERAGE MONTHLY RETURN</span>
              <p className={styles.statValue}>{spread(returns.stats.monthly)}</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Monthly returns ──
          Always laid out, so the calendar is on the page before there is a year
          to put in it — the trade tables below already behave this way. */}
      <div className={`${styles.tableWrap} ${styles.desktopBlock}`}>
        <table className={`${styles.table} ${styles.monthTable}`}>
          <thead>
            <tr>
              <th className={styles.th} />
              {MONTHS.map(m => <th key={m} className={`${styles.th} ${styles.numCol}`}>{m}</th>)}
              <th className={`${styles.th} ${styles.numCol}`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {returns.years.length === 0 ? (
              <tr>
                <td colSpan={14} className={styles.emptyCell}>No monthly returns yet</td>
              </tr>
            ) : (
              <>
              {returns.years.map(y => (
                <tr key={y.year} className={styles.row}>
                  <td className={`${styles.td} ${styles.yearCell}`}>{y.year}</td>
                  {y.months.map((v, i) => (
                    <td key={MONTHS[i]} className={`${styles.td} ${styles.numCol} ${toneOf(v)}`}>
                      {v == null ? '-' : pct(v)}
                    </td>
                  ))}
                  <td className={`${styles.td} ${styles.numCol} ${toneOf(y.total)}`}>{pct(y.total)}</td>
                </tr>
              ))}
              {/* Every year compounded into one figure, in the Total column it
                  belongs to rather than as a fourteenth column of its own. */}
              <tr className={styles.row}>
                <td className={styles.td} colSpan={12} />
                <td className={`${styles.td} ${styles.totalLabel} ${styles.numCol}`}>Total</td>
                <td className={`${styles.td} ${styles.numCol} ${styles.totalValue} ${toneOf(returns.total)}`}>
                  {pct(returns.total)}
                </td>
              </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* ── Trades ── */}
      <div className={`${styles.tradeTabs} ${styles.desktopBlock}`} role="tablist">
        <button
          role="tab"
          aria-selected={tab === 'open'}
          className={`${styles.tradeTab} ${tab === 'open' ? styles.tradeTabActive : ''}`}
          onClick={() => switchTab('open')}
        >
          Open positions ({openCount == null ? '-' : openCount})
        </button>
        <button
          role="tab"
          aria-selected={tab === 'history'}
          className={`${styles.tradeTab} ${tab === 'history' ? styles.tradeTabActive : ''}`}
          onClick={() => switchTab('history')}
        >
          Trade history
        </button>
      </div>

      <div className={`${styles.tableWrap} ${styles.desktopBlock}`}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Symbol</th>
              <th className={styles.th}>{tab === 'open' ? 'Open time' : 'Close time'}</th>
              <th className={`${styles.th} ${styles.numCol}`}>Price</th>
              <th className={`${styles.th} ${styles.numCol}`}>Lots</th>
              <th className={`${styles.th} ${styles.numCol}`}>Commissions</th>
              <th className={`${styles.th} ${styles.numCol}`}>Swap</th>
              <th className={`${styles.th} ${styles.numCol}`}>Trade P/L</th>
            </tr>
          </thead>
          <tbody>
            {tradesLoading ? (
              <tr><td colSpan={7} className={styles.emptyCell}>Loading trades…</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={7} className={styles.emptyCell}>
                  {tab === 'open' ? 'No open positions' : 'No closed trades yet'}
                </td>
              </tr>
            ) : rows.map(t => (
              <tr key={t.id} className={styles.row}>
                <td className={styles.td}>
                  <span className={`${styles.side} ${t.type === 'sell' ? styles.sideSell : styles.sideBuy}`}>
                    {t.type === 'sell' ? 'SELL' : 'BUY'}
                  </span>
                  <span className={styles.symbol}>{t.symbol}</span>
                </td>
                <td className={styles.td}>{tradeTime(tab === 'open' ? t.openTime : t.closeTime)}</td>
                <td className={`${styles.td} ${styles.numCol}`}>{price(tab === 'open' ? t.openPrice : t.closePrice)}</td>
                <td className={`${styles.td} ${styles.numCol}`}>{lotsText(t.lots)}</td>
                <td className={`${styles.td} ${styles.numCol}`}>{signedDollars(t.commission)}</td>
                <td className={`${styles.td} ${styles.numCol}`}>{dollars(t.swap)}</td>
                <td className={`${styles.td} ${styles.numCol} ${toneOf(t.profit)}`}>{dollars(t.profit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination page={tradesPage} totalPages={trades.pages} onPageChange={setTradesPage} variant="outline" />
      </div>

      {children}

      {/* The chart and the tables need the width to be readable, so a phone gets
          the figures above and a pointer to the desktop view instead. */}
      <div className={`${styles.deskHint} ${styles.mobileOnly}`}>In-depth view on desktop</div>
    </div>
  );
}

export default PowIndex;
